/* eslint-disable no-restricted-imports */
import React, {useEffect, useState} from 'react'
import styles from './UIGameTradeOffers.module.scss'
import clsx from 'clsx'
import {AnimatePresence, motion} from 'framer-motion'
import {UIGameTradeOfferHeader} from './UIGameTradeOfferHeader'
import {UIGameTradeOfferActiveOffer} from './UIGameTradeOfferActiveOffer'
import {TradeId, TradeResponseStatus} from '@colonist/game-states'
import {useUIGameSocketContext} from '../../contexts/UIGameSocketContext'
import {GameStateEnums} from '../../../../../code/shared/GameEnums'
import {ModeSettingEnum} from '@colonist/types'
import {TradeErrorPayload} from '@colonist/game-mechanic-validators'
import {useGameStore} from '../../store/GameStoreContext'
import {useIsReplay} from '../../store/selectors/useIsReplay'

export interface UIGameTradeOffersProps {
    className?: string | undefined
    disableTooltips?: boolean
}

export const UIGameTradeOffers = ({className, disableTooltips}: UIGameTradeOffersProps) => {
    const tradeState = useGameStore((state) => state.gameState.tradeState)
    const myColor = useGameStore((state) => state.myColor)
    const modeSetting = useGameStore((state) => state.gameSettings.modeSetting)
    const {registerUIGameMechanic} = useUIGameSocketContext()
    const isReplay = useIsReplay()
    const isTradeOffersVisible = useGameStore((state) => state.gameTradeOffers.isTradeOffersVisible)
    const setIsTradeOffersVisible = useGameStore((state) => state.gameTradeOffers.setIsTradeOffersVisible)
    const [highlightedTradeIds, setHighlightedTradeIds] = useState<TradeId[]>([])
    const [isGameInBackground, setIsGameInBackground] = useState(false)
    const turnState = useGameStore((state) => state.gameState.currentState.turnState)
    const isRushMode = modeSetting === ModeSettingEnum.ColonistRush4P

    const handleHighlightTradeOffer = (tradeErrorPayload: TradeErrorPayload) => {
        setHighlightedTradeIds(tradeErrorPayload.highlightTrade?.tradeIds || [])
    }

    const handleGameVisibilityUpdated = (isGameVisible: boolean) => {
        const gameInBackground = isGameVisible == false
        setIsGameInBackground(gameInBackground)
    }

    useEffect(() => {
        registerUIGameMechanic({
            onEvent: (data: any, eventType: GameStateEnums.UIGameEventEnums) => {
                switch(eventType) {
                    case GameStateEnums.UIGameEventEnums.HighlightTradeOffer: handleHighlightTradeOffer(data); break
                    case GameStateEnums.UIGameEventEnums.GameVisibilityUpdated: handleGameVisibilityUpdated(data); break
                    default: break
                }
            },
        })
    }, [])

    const onShowHideTradeClick = () => {
        setIsTradeOffersVisible(!isTradeOffersVisible)
    }

    const shouldFilterOutRejectedTradeOffers = isRushMode

    const activeOffers = Object.values(tradeState.activeOffers)
        .filter((offer) => offer !== undefined)
        .filter((offer) => {
            if(shouldFilterOutRejectedTradeOffers) {
                const myResponse = offer.playerResponses[myColor]
                if(myResponse === TradeResponseStatus.Rejected) return false
            }
            return true
        })

    useEffect(() => {
        if(isRushMode) return

        setIsTradeOffersVisible(true)
    }, [turnState, isRushMode])

    if(activeOffers.length === 0 || isReplay) return null

    return (
        <div className={clsx(styles.container, className)}>
            <UIGameTradeOfferHeader
                onShowHideTradeClick={onShowHideTradeClick}
                isShowingTrade={isTradeOffersVisible}
            />
            <AnimatePresence>
                {activeOffers.map((offer) => (
                    <motion.div
                        key={offer.id}
                        hidden={!isTradeOffersVisible}
                        initial={{opacity: 0}}
                        animate={{
                            opacity: highlightedTradeIds.includes(offer.id)
                                ? [1, 0.5, 1, 0.5, 1]
                                : 1,
                        }}
                        exit={{opacity: 0}}
                        transition={
                            highlightedTradeIds.includes(offer.id)
                                ? {duration: 1, times: [0, 0.25, 0.5, 0.75, 1]}
                                : {duration: 0.3}
                        }
                        onAnimationComplete={() => {
                            if(highlightedTradeIds.includes(offer.id)) {
                                setHighlightedTradeIds([])
                            }
                        }}
                    >
                        <UIGameTradeOfferActiveOffer
                            offer={offer}
                            isGameInBackground={isGameInBackground}
                            disableTooltips={disableTooltips} />
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    )
}
